import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Mail, Package, Gift, Copy, Check, CreditCard, ExternalLink, Wallet } from 'lucide-react';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { getReturnRequest, getPublicSettings } from '@/lib/api';
import { Truck } from 'lucide-react';
import { toast } from 'sonner';

const Confirmation = () => {
  const { requestId } = useParams();
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [publicSettings, setPublicSettings] = useState({});

  useEffect(() => {
    const loadRequest = async () => {
      try {
        const [data, settings] = await Promise.all([
          getReturnRequest(requestId),
          getPublicSettings().catch(() => ({}))
        ]);
        setRequest(data);
        setPublicSettings(settings);
      } catch (error) {
        console.error('Error loading request:', error);
      }
      setIsLoading(false);
    };
    
    loadRequest();
    
    // Clear session storage
    sessionStorage.removeItem('currentOrder');
    sessionStorage.removeItem('selectedItems');
    sessionStorage.removeItem('requestType');
    sessionStorage.removeItem('reason');
  }, [requestId]);

  const copyVoucherCode = () => {
    if (request?.gift_card?.code) {
      navigator.clipboard.writeText(request.gift_card.code);
      setCopied(true);
      toast.success('Codice copiato!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case 'exchange': return 'Cambio';
      case 'voucher': return 'Voucher';
      case 'other': return 'Altra Richiesta';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <div className="page-container bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p>Caricamento...</p>
        </main>
      </div>
    );
  }

  const isExchange = request?.request_type === 'exchange';
  const hasPaymentLink = isExchange && request?.draft_order?.invoice_url && request?.total_to_pay > 0;
  const hasRefund = isExchange && request?.refund_amount > 0;

  return (
    <div className="page-container bg-white">
      <Header />
      
      <main className="flex-1 py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className="mb-8"
          >
            <div className="w-20 h-20 mx-auto bg-primary text-white flex items-center justify-center">
              <CheckCircle className="w-10 h-10" />
            </div>
          </motion.div>
          
          <motion.h1 
            className="font-heading text-4xl md:text-5xl font-normal tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {isExchange ? 'Quasi fatto!' : 'Richiesta Inviata'}
          </motion.h1>
          
          <motion.p 
            className="text-lg text-muted-foreground font-light mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {isExchange 
              ? hasRefund
                ? 'Il tuo cambio è stato confermato. Riceverai un rimborso!'
                : hasPaymentLink
                  ? 'Completa il pagamento per confermare il tuo cambio'
                  : 'Il tuo cambio è stato confermato'
              : `La tua richiesta di ${getRequestTypeLabel(request?.request_type).toLowerCase()} è stata ricevuta`
            }
          </motion.p>

          {/* EXCHANGE REFUND - Monetary refund */}
          {hasRefund && (
            <motion.div 
              className="bg-blue-50 border-2 border-blue-300 p-8 mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <Wallet className="w-12 h-12 mx-auto text-blue-600 mb-4" />
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-blue-600 mb-3">
                Rimborso in Arrivo
              </p>
              <p className="text-4xl font-bold text-blue-700 mb-4">
                €{parseFloat(request.refund_amount).toFixed(2)}
              </p>
              <div className="text-sm mb-4 space-y-1 text-blue-700">
                <p>Costi di gestione: €{parseFloat(request.exchange_fee).toFixed(2)}</p>
                <p>Differenza prezzo (a tuo favore): €{Math.abs(parseFloat(request.price_difference)).toFixed(2)}</p>
              </div>
              <p className="text-sm p-4 border bg-white text-blue-600 border-blue-200">
                Il rimborso verrà elaborato dopo che avremo ricevuto e verificato il prodotto reso. Ti contatteremo via email per comunicarti le modalità.
              </p>
            </motion.div>
          )}

          {/* EXCHANGE PAYMENT - Big and prominent */}
          {hasPaymentLink && (
            <motion.div 
              className="bg-amber-50 border-2 border-amber-300 p-8 mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <CreditCard className="w-12 h-12 mx-auto text-amber-600 mb-4" />
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-amber-600 mb-3">
                Pagamento richiesto
              </p>
              <p className="text-4xl font-bold text-amber-800 mb-4">
                €{parseFloat(request.total_to_pay).toFixed(2)}
              </p>
              <div className="text-sm text-amber-700 mb-6 space-y-1">
                <p>Costi di gestione: €{parseFloat(request.exchange_fee).toFixed(2)}</p>
                {request.price_difference > 0 && (
                  <p>Differenza prezzo: €{parseFloat(request.price_difference).toFixed(2)}</p>
                )}
              </div>
              
              {/* Warning Box */}
              <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  ⚠️ Importante prima di procedere:
                </p>
                <p className="text-sm text-amber-700">
                  Verrai reindirizzato sulla pagina di pagamento. <strong>Non modificare modalità di spedizione e indirizzo di consegna</strong> per non incorrere in problemi di sistema. Per eventuali comunicazioni, contatta l'assistenza dopo il pagamento.
                </p>
              </div>
              
              <a 
                href={request.draft_order.invoice_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 h-auto"
                  data-testid="pay-now-btn"
                >
                  <CreditCard className="w-5 h-5 mr-3" />
                  Paga Ora
                  <ExternalLink className="w-4 h-4 ml-3" />
                </Button>
              </a>
              <p className="text-xs text-amber-600 mt-4">
                Checkout sicuro Shopify
              </p>
            </motion.div>
          )}

          {/* VOUCHER - Pending approval (no gift card yet) */}
          {request?.request_type === 'voucher' && !request?.gift_card && (
            <motion.div 
              className="bg-emerald-50 border-2 border-emerald-300 p-8 mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <Gift className="w-12 h-12 mx-auto text-emerald-600 mb-4" />
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-emerald-600 mb-3">
                Voucher in attesa
              </p>
              <p className="text-3xl font-bold text-emerald-700 mb-4">
                €{request?.voucher_value?.toFixed(2)}
              </p>
              <p className="text-sm text-emerald-700 mb-6">
                Una volta ricevuto il tuo reso, il nostro team Controllo Qualità verificherà che il prodotto sia integro. Dopo l'approvazione, riceverai il codice buono via email oppure potrai visualizzarlo accedendo a questo portale con i dati del tuo ordine.
              </p>
              {(() => {
                const dm = request?.delivery_method;
                const returnAddress = publicSettings.return_shipping_address || {};
                const storeAddress = publicSettings.store_address || returnAddress;
                if (dm === 'store') {
                  return (
                    <div className="bg-white p-4 border border-emerald-200 text-left">
                      <p className="text-xs font-bold tracking-[0.15em] uppercase text-emerald-600 mb-2">
                        Consegna in negozio
                      </p>
                      <p className="text-sm text-emerald-700 mb-2">Potrai consegnare il pacco al negozio dopo la conferma.</p>
                      <p className="font-medium text-sm">{storeAddress.name || 'DEA FEA'}</p>
                      <p className="text-sm text-muted-foreground">{storeAddress.street}</p>
                      <p className="text-sm text-muted-foreground">{storeAddress.zip} {storeAddress.city} ({storeAddress.province})</p>
                      <p className="text-sm text-muted-foreground">{storeAddress.country || 'Italia'}</p>
                    </div>
                  );
                }
                // label, free, pickup — show return shipping address
                return returnAddress.street ? (
                  <div className="bg-white p-4 border border-emerald-200 text-left">
                    <p className="text-xs font-bold tracking-[0.15em] uppercase text-emerald-600 mb-2">
                      Spedisci il pacco a:
                    </p>
                    <p className="font-medium text-sm">{returnAddress.name || 'DEA FEA'}</p>
                    <p className="text-sm text-muted-foreground">{returnAddress.street}</p>
                    <p className="text-sm text-muted-foreground">{returnAddress.zip} {returnAddress.city} ({returnAddress.province})</p>
                    <p className="text-sm text-muted-foreground">{returnAddress.country || 'Italia'}</p>
                  </div>
                ) : null;
              })()}
            </motion.div>
          )}

          {/* VOUCHER CODE - Only shown when approved (gift card exists) */}
          {request?.request_type === 'voucher' && request?.gift_card && (
            <motion.div 
              className="bg-emerald-50 border-2 border-emerald-300 p-8 mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <Gift className="w-12 h-12 mx-auto text-emerald-600 mb-4" />
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-emerald-600 mb-3">
                Il tuo codice voucher
              </p>
              <div className="flex items-center justify-center gap-3 mb-4">
                <code className="text-3xl md:text-4xl font-mono font-bold text-emerald-800 tracking-wider">
                  {request.gift_card.code?.toUpperCase()}
                </code>
                <button 
                  onClick={copyVoucherCode}
                  className="p-2 hover:bg-emerald-100 rounded transition-colors"
                  title="Copia codice"
                >
                  {copied ? (
                    <Check className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <Copy className="w-6 h-6 text-emerald-600" />
                  )}
                </button>
              </div>
              <p className="text-2xl font-bold text-emerald-700 mb-2">
                €{parseFloat(request.gift_card.initial_value).toFixed(2)}
              </p>
              <p className="text-sm text-emerald-600">
                Utilizza questo codice al checkout su deafea.it
              </p>
              {request.gift_card.expires_on && (
                <p className="text-xs text-emerald-500 mt-2">
                  Valido fino al {new Date(request.gift_card.expires_on).toLocaleDateString('it-IT')}
                </p>
              )}
            </motion.div>
          )}

          <motion.div 
            className="luxury-card p-8 mb-12 text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="space-y-6">
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Codice Richiesta
                </p>
                <p className="font-mono text-lg">{requestId?.slice(0, 8).toUpperCase()}</p>
              </div>
              
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Tipo Richiesta
                </p>
                <p>{getRequestTypeLabel(request?.request_type)}</p>
              </div>

              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Ordine
                </p>
                <p>{request?.order_number}</p>
              </div>

              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  {request?.request_type === 'voucher' ? 'Articoli per Voucher' : 'Prodotti da Rendere'}
                </p>
                <div className="space-y-2">
                  {request?.return_items?.map((item, i) => (
                    <p key={item.variant_id || item.line_item_id || `ret-${i}`} className="text-sm">{item.title} - {item.variant_title}</p>
                  ))}
                </div>
              </div>

              {request?.exchange_items && request.exchange_items.length > 0 && (
                <div>
                  <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                    Prodotti Sostitutivi
                  </p>
                  <div className="space-y-2">
                    {request.exchange_items.map((item, i) => (
                      <p key={item.variant_id || `exc-${i}`} className="text-sm">{item.title} - {item.variant_title}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Modalità Restituzione — dinamica */}
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Modalità Restituzione
                </p>
                {(() => {
                  const dm = request?.delivery_method;
                  const labels = publicSettings.delivery_method_labels || {};
                  const returnAddress = publicSettings.return_shipping_address || {};
                  const storeAddress = publicSettings.store_address || returnAddress;

                  if (dm === 'store') {
                    return (
                      <div>
                        <div className="bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
                          <Truck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-emerald-800">Consegna in negozio</p>
                            <p className="text-sm text-emerald-700">Potrai consegnare il pacco al negozio dopo la conferma.</p>
                          </div>
                        </div>
                        {storeAddress.street && (
                          <div className="mt-3 bg-gray-50 border border-gray-200 p-4">
                            <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">Indirizzo del negozio</p>
                            <p className="font-medium text-sm">{storeAddress.name || 'DEA FEA'}</p>
                            <p className="text-sm text-muted-foreground">{storeAddress.street}</p>
                            <p className="text-sm text-muted-foreground">{storeAddress.zip} {storeAddress.city} ({storeAddress.province})</p>
                            <p className="text-sm text-muted-foreground">{storeAddress.country || 'Italia'}</p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // pickup / label / free — use configured method
                  const method = dm || (
                    request?.request_type === 'exchange'
                      ? (publicSettings.shipping_method_exchange || 'pickup')
                      : (publicSettings.shipping_method_voucher || 'pickup')
                  );
                  const methodLabel = labels[`${method}_label`] || (method === 'pickup' ? 'Ritiro a domicilio' : method === 'label' ? 'Spedizione autonoma' : 'Spedizione guidata');
                  const methodDesc = labels[`${method}_description`] || '';

                  return (
                    <div>
                      <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
                        <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-800">{methodLabel}</p>
                          {methodDesc && <p className="text-sm text-blue-700">{methodDesc}</p>}
                        </div>
                      </div>
                      {(method === 'label' || method === 'free') && returnAddress.street && (
                        <div className="mt-3 bg-gray-50 border border-gray-200 p-4">
                          <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">Indirizzo per la Spedizione</p>
                          <p className="font-medium text-sm">{returnAddress.name || 'DEA FEA'}</p>
                          <p className="text-sm text-muted-foreground">{returnAddress.street}</p>
                          <p className="text-sm text-muted-foreground">{returnAddress.zip} {returnAddress.city} ({returnAddress.province})</p>
                          <p className="text-sm text-muted-foreground">{returnAddress.country || 'Italia'}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-6 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="luxury-card p-6 text-left">
              <Mail className="w-6 h-6 mb-4 text-muted-foreground" />
              <h3 className="font-medium mb-2">Conferma via Email</h3>
              <p className="text-sm text-muted-foreground">
                Riceverai una email di conferma con tutti i dettagli della richiesta
              </p>
            </div>
            
            <div className="luxury-card p-6 text-left">
              <Package className="w-6 h-6 mb-4 text-muted-foreground" />
              <h3 className="font-medium mb-2">Prossimi Passi</h3>
              <p className="text-sm text-muted-foreground">
                {(() => {
                  const dm = request?.delivery_method;
                  if (dm === 'store') {
                    return 'Dopo la conferma, potrai consegnare il pacco direttamente in negozio.';
                  }
                  const method = dm || (
                    isExchange 
                      ? (publicSettings.shipping_method_exchange || 'pickup')
                      : (publicSettings.shipping_method_voucher || 'pickup')
                  );
                  if (method === 'pickup') {
                    if (isExchange) {
                      return hasRefund
                        ? 'Organizzeremo il ritiro del tuo pacco. Dopo la verifica, elaboreremo il rimborso.'
                        : hasPaymentLink
                          ? 'Dopo il pagamento, organizzeremo il ritiro del tuo pacco.'
                          : 'Organizzeremo il ritiro del tuo pacco.';
                    }
                    return request?.request_type === 'voucher'
                      ? 'Organizzeremo il ritiro del tuo pacco. Dopo il Controllo Qualità, riceverai il buono via email.'
                      : 'Attendi l\'approvazione, poi riceverai le istruzioni per la spedizione';
                  }
                  // label or free — customer ships
                  if (isExchange) {
                    return hasRefund
                      ? 'Spedisci il pacco all\'indirizzo indicato. Dopo la verifica, elaboreremo il rimborso.'
                      : hasPaymentLink
                        ? 'Dopo il pagamento, spedisci il pacco all\'indirizzo indicato.'
                        : 'Spedisci il pacco all\'indirizzo indicato.';
                  }
                  return request?.request_type === 'voucher'
                    ? 'Spedisci il pacco all\'indirizzo indicato. Dopo il Controllo Qualità, riceverai il buono via email.'
                    : 'Attendi l\'approvazione, poi riceverai le istruzioni per la spedizione';
                })()}
              </p>
            </div>
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {hasPaymentLink && (
              <div className="flex flex-col items-center gap-2">
                <a 
                  href={request.draft_order.invoice_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button className="luxury-btn-primary inline-flex items-center gap-3" data-testid="pay-btn-secondary">
                    Completa Pagamento
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
                <p className="text-xs text-muted-foreground">
                  Non modificare spedizione/indirizzo
                </p>
              </div>
            )}
            
            <Link to="/">
              <Button variant="outline" className="luxury-btn-secondary" data-testid="new-request-btn">
                Nuova Richiesta
              </Button>
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Confirmation;
